import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GjeFetchError } from '@gjensidige/service-async/lib/types/errors/GjeFetchError';

import {
  AsyncChatConfig,
  CallMeRouting,
  DirectEntrance
} from '../../types/async-config';
import { ConfigEvent } from '../../types/call-guide/events';
import { ChatStickyConfig } from '../../types/initial-config';
import { requestConfig } from '../thunks/config';

export type ShortNameAndEntrance = {
  shortName: string;
  entrance: string | undefined;
};

export type ConfigState = ChatStickyConfig &
  AsyncChatConfig &
  ConfigEvent & {
    isFetching: boolean;
    error?: GjeFetchError;
    shortNames: string[];
    shortNamesAndEntrances: ShortNameAndEntrance[];
    serviceQueuesShortNames: string[];
    serviceQueuesShortNamesAndEntrances: ShortNameAndEntrance[];
    selectedEntrance?: DirectEntrance;
  };

const initialState: ConfigState = {
  chatEntryMaxLength: 1000,
  chatQueues: [],
  shortNames: [],
  shortNamesAndEntrances: [],
  serviceQueuesShortNames: [],
  serviceQueuesShortNamesAndEntrances: [],
  chatArchives: [],
  serviceQueues: [],
  isFetching: false
};

const config = createSlice({
  name: 'config',
  initialState,
  reducers: {
    receiveConfig(
      state,
      action: PayloadAction<ChatStickyConfig | AsyncChatConfig | ConfigEvent>
    ) {
      return { ...state, ...action.payload };
    },
    selectDirectEntrance(state, action: PayloadAction<DirectEntrance>) {
      state.selectedEntrance = action.payload;
      state.errand = action.payload.errand;
      state.entrance = action.payload.entrance;
    },
    setShortNames(state, action: PayloadAction<string[]>) {
      state.shortNames = action.payload;
    },
    setShortNamesAndEntrances(
      state,
      action: PayloadAction<ShortNameAndEntrance[]>
    ) {
      state.shortNamesAndEntrances = action.payload;
    },
    setServiceQueuesShortNames(state, action: PayloadAction<string[]>) {
      state.serviceQueuesShortNames = action.payload;
    },
    setServiceQueuesShortNamesAndEntrances(
      state,
      action: PayloadAction<ShortNameAndEntrance[]>
    ) {
      state.serviceQueuesShortNamesAndEntrances = action.payload;
    },
    // TODO: Remove this action
    // setCallMeQueue(state, action: PayloadAction<string>) {
    //   state.callMeQueue = action.payload;
    // },
    setCallMeRouting(state, action: PayloadAction<CallMeRouting>) {
      state.callMeRouting = action.payload;
    },
    setShowDigitalContactLink(state, action: PayloadAction<boolean>) {
      state.showDigitalContactLink = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestConfig.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(requestConfig.fulfilled, (state, action) => ({
        ...state,
        ...action.payload,
        isFetching: false,
        error: undefined
      }))
      .addCase(requestConfig.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      });
  }
});

export const {
  receiveConfig,
  selectDirectEntrance,
  setShortNames,
  setShortNamesAndEntrances,
  setServiceQueuesShortNames,
  setServiceQueuesShortNamesAndEntrances,
  setCallMeRouting,
  setShowDigitalContactLink
} = config.actions;

export default config.reducer;
